import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

const PAGE_HEIGHT_PT = 792; // 11in
const PAGE_WIDTH_PT = 612;  // 8.5in
const MARGIN_PT = 72;       // 1in
const USABLE_HEIGHT = PAGE_HEIGHT_PT - 2 * MARGIN_PT;
const LINE_HEIGHT_PT = 16;

const ptToPx = pt => pt * 1.33;

function PaginatedPreview({
  content = '',
  fontFamily = 'helvetica',
  clientName = '',
  reportTitle = '',
  logoUrl = '',
  footerHeightPt = 54,
  coverImageUrl = '',
  isCover = false,
  projectName = '',
  sectionName = ''
}) {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    const parsed = parseCustomImageMarkdown(content);
    const brokenIntoPages = paginateContent(parsed);
    setPages(brokenIntoPages);
  }, [content]);

  const parseCustomImageMarkdown = (text) => {
    const regex = /\[\[image:(.*?)\|(.*?)\]\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, url, caption] = match;
      const index = match.index;

      if (index > lastIndex) {
        parts.push({ type: 'markdown', text: text.slice(lastIndex, index) });
      }

      parts.push({ type: 'image', url, caption });
      lastIndex = index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      parts.push({ type: 'markdown', text: text.slice(lastIndex) });
    }

    return parts;
  };

  const paginateContent = (blocks) => {
    const pageBlocks = [];
    let currentPage = [];
    let currentHeight = 0;

    const estimateBlockHeight = (block) => {
      if (block.type === 'markdown') {
        const lines = block.text.split('\n').filter(Boolean);
        return lines.length * LINE_HEIGHT_PT;
      } else if (block.type === 'image') {
        return 180 + 18; // estimated image + caption height
      }
      return 0;
    };

    blocks.forEach(block => {
      const height = estimateBlockHeight(block);
      if (currentHeight + height > USABLE_HEIGHT && currentPage.length > 0) {
        pageBlocks.push(currentPage);
        currentPage = [block];
        currentHeight = height;
      } else {
        currentPage.push(block);
        currentHeight += height;
      }
    });

    if (currentPage.length > 0) pageBlocks.push(currentPage);
    return pageBlocks;
  };

  const headingStyle = {
    fontSize: '22pt',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '1rem'
  };

  const subheadingStyle = {
    fontSize: '14pt',
    textAlign: 'center',
    marginBottom: '1rem'
  };

  const paragraphStyle = {
    fontSize: '12pt',
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
    marginBottom: '0.25rem'
  };

  const footerLogoStyle = {
    height: `${ptToPx(footerHeightPt)}px`,
    objectFit: 'contain',
    display: 'block',
    margin: '2rem auto 0 auto'
  };

  const pageStyle = {
    width: `${ptToPx(PAGE_WIDTH_PT)}px`,
    height: `${ptToPx(PAGE_HEIGHT_PT)}px`,
    padding: `${ptToPx(MARGIN_PT)}px`,
    backgroundColor: 'white',
    boxShadow: '0 0 4px rgba(0,0,0,0.2)',
    margin: '1rem auto',
    fontFamily,
    position: 'relative',
    overflow: 'hidden'
  };

  return (
    <div>
      {isCover ? (
        <div style={pageStyle}>
          <div style={headingStyle}>{projectName || 'Untitled Project'}</div>
          {reportTitle && <div style={subheadingStyle}>{reportTitle}</div>}
          {coverImageUrl && (
            <img
              src={coverImageUrl}
              alt="Cover"
              style={{
                maxWidth: '5in',
                maxHeight: `${5 * (4032 / 3024)}in`,
                objectFit: 'contain',
                display: 'block',
                margin: '1rem auto'
              }}
            />
          )}
          {clientName && (
            <div style={{ ...paragraphStyle, fontWeight: 'bold' }}>{clientName}</div>
          )}
          {content && <div style={paragraphStyle}>{content}</div>}
          {logoUrl && <img src={logoUrl} alt="Logo" style={footerLogoStyle} />}
        </div>
      ) : (
        pages.map((blocks, i) => (
          <div key={i} style={pageStyle}>
            {i === 0 && sectionName && (
              <h2 style={{
                fontSize: '18pt',
                fontWeight: 'bold',
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                {sectionName}
              </h2>
            )}

            {blocks.map((block, idx) =>
              block.type === 'image' ? (
                <div key={idx} style={{ textAlign: 'center', margin: '1rem 0' }}>
                  <img
                    src={block.url}
                    alt={block.caption}
                    style={{
                      maxWidth: '5in',
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                  <div style={{ fontSize: '11pt', marginTop: '0.3rem', fontStyle: 'italic' }}>
                    {block.caption}
                  </div>
                </div>
              ) : (
                <ReactMarkdown
                  key={idx}
                  children={block.text}
                  components={{
                    h1: ({ node, ...props }) => <h1 style={{ fontSize: '20pt', marginBottom: '0.75rem' }} {...props} />,
                    h2: ({ node, ...props }) => <h2 style={{ fontSize: '16pt', marginBottom: '0.5rem' }} {...props} />,
                    p: ({ node, ...props }) => <p style={{ fontSize: '12pt', marginBottom: '0.25rem' }} {...props} />,
                    ul: ({ node, ...props }) => <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }} {...props} />,
                    ol: ({ node, ...props }) => <ol style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }} {...props} />,
                    li: ({ node, ...props }) => <li style={{ fontSize: '12pt', marginBottom: '0.25rem' }} {...props} />
                  }}
                />
              )
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default PaginatedPreview;
